import { watch } from 'vue';
import { defineStore, acceptHMRUpdate } from 'pinia';
import type { FrontendPlatformConfig } from '@/api/platformConfig';
import {
  useFrontendPlatformConfigQuery,
  useInvalidateFrontendPlatformConfig,
} from '@/queries/platformConfig';

const STORAGE_KEY = 'platformConfig';

function readStoredConfig(): FrontendPlatformConfig | undefined {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
}

// The store owns the single observer so polling starts once at boot rather than from each read.
export const usePlatformConfigStore = defineStore('platformConfig', () => {
  // The last response stands in until this page load's first fetch lands, so synchronous reads on first paint see real values.
  const { platformConfig, query } = useFrontendPlatformConfigQuery({
    placeholder: readStoredConfig,
  });
  const refresh = useInvalidateFrontendPlatformConfig();

  watch(query.data, (data) => {
    if (!data || query.isPlaceholderData.value) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  });

  return { platformConfig, refresh };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(usePlatformConfigStore, import.meta.hot)
  );
}
